import React from 'react';
import { 
  DocumentTextIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TagIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { DocumentStorage } from '../../types/documentStorage';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { useDocumentTags } from '../../hooks/useDocumentTags';

interface DocumentStorageViewProps {
  document: DocumentStorage;
  onClose: () => void;
  onEdit: () => void;
}

export default function DocumentStorageView({ document, onClose, onEdit }: DocumentStorageViewProps) {
  const { tags: documentTags } = useDocumentTags();
  
  const handleDownload = () => {
    if (document.downloadUrl) {
      // Create a temporary anchor element to trigger download
      const link = window.document.createElement('a');
      link.href = document.downloadUrl;
      link.download = document.fileName || document.title;
      link.target = '_blank';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#16a34a'; // green-600 - megfelelően működő
      case 'archived': return '#94a3b8'; // slate-400 - archivált elemek
      case 'expired': return '#dc2626'; // red-600 - lejárt elemek
      case 'draft': return '#eab308'; // yellow-500 - piszkozat
      default: return '#6b7280'; // gray-500 - alapértelmezett
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktív';
      case 'archived': return 'Archivált';
      case 'expired': return 'Lejárt';
      case 'draft': return 'Piszkozat';
      default: return status;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isExpired = document.expirationDate && document.expirationDate < new Date();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <DocumentTextIcon className="h-6 w-6 text-gray-400" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {document.title}
            </h1>
          </div>
          {document.description && (
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {document.description}
            </p>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span
            className="inline-flex px-3 py-1 text-sm font-semibold rounded-full"
            style={{ 
              backgroundColor: getStatusColor(document.status) + '20',
              color: getStatusColor(document.status)
            }}
          >
            {getStatusText(document.status)}
          </span>
          {isExpired && (
            <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
              Lejárt
            </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Document Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Alapinformációk
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Kategória
                  </div>
                  {document.category ? (
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: document.category.color }}
                      ></div>
                      <span className="text-gray-900 dark:text-white">
                        {document.category.name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400 italic">
                      Nincs kategória
                    </span>
                  )}
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Ügyfél
                  </div>
                  <div className="flex items-center text-gray-900 dark:text-white">
                    <UserIcon className="h-4 w-4 mr-1" />
                    {document.customerName}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Láthatóság
                  </div>
                  <div className="flex items-center text-gray-900 dark:text-white">
                    {document.isPublic ? (
                      <>
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Nyilvános
                      </>
                    ) : (
                      <>
                        <EyeSlashIcon className="h-4 w-4 mr-1" />
                        Privát
                      </>
                    )}
                  </div>
                </div>

                {document.creationDate && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Kelt dátum
                    </div>
                    <div className="flex items-center text-gray-900 dark:text-white">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {document.creationDate.toLocaleDateString('hu-HU')}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Verzió
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    v{document.version}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* File Information */}
          {document.fileName && (
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Fájl információk
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400" />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {document.fileName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {formatFileSize(document.fileSize)} • {document.fileType}
                        </div>
                      </div>
                    </div>
                    {document.downloadUrl && (
                      <Button
                        onClick={handleDownload}
                        size="sm"
                        className="flex items-center space-x-2"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        <span>Letöltés</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Tags */}
          {document.tags && document.tags.length > 0 && (
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Címkék
                </h3>
                <div className="flex flex-wrap gap-2">
                  {document.tags.map((tagId, index) => {
                    const tagObj = documentTags.find((t: any) => t.id === tagId);
                    const displayName = tagObj?.title || tagObj?.name || tagId;
                    const tagColor = tagObj?.color || '#6b7280';
                    return (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm text-white font-medium shadow-sm"
                        style={{
                          backgroundColor: tagColor,
                          color: 'white'
                        }}
                        title={displayName}
                      >
                        <TagIcon className="h-4 w-4 mr-1" />
                        {displayName}
                      </span>
                    );
                  })}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card>
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Műveletek
              </h3>
              <div className="space-y-3">
                <Button
                  onClick={onEdit}
                  className="w-full flex items-center justify-center space-x-2"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                  <span>Szerkesztés</span>
                </Button>
                
                {document.downloadUrl && (document.fileType === 'application/pdf' || document.fileType?.startsWith('image/')) && (
                  <Button
                    onClick={() => window.open(document.downloadUrl, '_blank')}
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                  >
                    <EyeIcon className="h-4 w-4" />
                    <span>Preview</span>
                  </Button>
                )}
                
                {document.downloadUrl && (
                  <Button
                    onClick={handleDownload}
                    variant="outline"
                    className="w-full flex items-center justify-center space-x-2"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    <span>Letöltés</span>
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Dates */}
          <Card>
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Dátumok
              </h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Létrehozva
                  </div>
                  <div className="flex items-center text-gray-900 dark:text-white">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {document.createdAt.toLocaleDateString('hu-HU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Módosítva
                  </div>
                  <div className="flex items-center text-gray-900 dark:text-white">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    {document.updatedAt.toLocaleDateString('hu-HU', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>

                {document.expirationDate && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      Lejárat
                    </div>
                    <div className={`flex items-center ${isExpired ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {document.expirationDate.toLocaleDateString('hu-HU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      {isExpired && (
                        <span className="ml-2 text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-0.5 rounded">
                          Lejárt
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Creator */}
          <Card>
            <div className="p-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Létrehozó
              </h3>
              <div className="flex items-center">
                <UserIcon className="h-8 w-8 text-gray-400 mr-3" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {document.createdByName}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {document.createdAt.toLocaleDateString('hu-HU')}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="outline"
          onClick={onClose}
        >
          Bezárás
        </Button>
        <Button onClick={onEdit}>
          Szerkesztés
        </Button>
      </div>
    </div>
  );
}
